import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { X, ChevronLeft, ChevronRight, Camera } from 'lucide-react'
import { galleryService } from '../services/database.js'

function Gallery() {
  const { language } = useLanguage()
  const [selectedImage, setSelectedImage] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [images, setImages] = useState([])

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const galleryData = await galleryService.getAll()
        if (galleryData && galleryData.length > 0) {
          setImages(galleryData)
        } else {
          // Fallback to default gallery if no data in database
          setImages([
            { id: 1, category: language === 'ar' ? 'معدات' : 'Equipment', title: language === 'ar' ? 'منطقة الأوزان الحرة' : 'Free Weights Area', image_url: null },
            { id: 2, category: language === 'ar' ? 'كارديو' : 'Cardio', title: language === 'ar' ? 'أجهزة الكارديو' : 'Cardio Machines', image_url: null },
            { id: 3, category: language === 'ar' ? 'مجموعات' : 'Groups', title: language === 'ar' ? 'حصص جماعية' : 'Group Classes', image_url: null },
            { id: 4, category: language === 'ar' ? 'معدات' : 'Equipment', title: language === 'ar' ? 'منطقة الآلات' : 'Machines Area', image_url: null },
            { id: 5, category: language === 'ar' ? 'مدربين' : 'Trainers', title: language === 'ar' ? 'فريق التدريب' : 'Training Team', image_url: null },
            { id: 6, category: language === 'ar' ? 'مرافق' : 'Facilities', title: language === 'ar' ? 'غرفة تبديل الملابس' : 'Locker Room', image_url: null },
          ])
        }
      } catch (error) {
        console.error('Error loading gallery:', error)
        // Fallback to localStorage if database fails
        const savedGallery = localStorage.getItem('oxygen_gallery')
        if (savedGallery) {
          setImages(JSON.parse(savedGallery))
        }
      }
    }
    
    // Load gallery immediately
    loadGallery()
    
    // Set up periodic sync to refresh data
    const syncInterval = setInterval(() => {
      if (navigator.onLine) {
        loadGallery()
      }
    }, 30000) // Sync every 30 seconds
    
    // Listen for visibility changes to sync when app becomes active
    const handleVisibilityChange = () => {
      if (!document.hidden && navigator.onLine) {
        loadGallery()
      }
    }
    
    // Listen for online events
    const handleOnline = () => {
      loadGallery()
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)
    
    return () => {
      clearInterval(syncInterval)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
    }
  }, [language])

  useEffect(() => {
    const handleStorageChange = () => {
      const savedGallery = localStorage.getItem('oxygen_gallery')
      if (savedGallery) {
        setImages(JSON.parse(savedGallery))
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length)
  }

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length)
  }

  return (
    <section id="gallery" className="py-24 bg-darker relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,215,0,0.03),transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block px-4 py-2 rounded-full bg-yellow-400/10 text-yellow-400 text-sm font-medium mb-4">
            {language === 'ar' ? 'معرض الصور' : 'Photo Gallery'}
          </span>
          <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
            {language === 'ar' ? 'استعرض ' : 'View '}<span className="text-gradient">{language === 'ar' ? 'مرافقنا' : 'Our Facilities'}</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {language === 'ar' ? 'جولة داخل نادي أوكسجين وتعرف على مرافقنا المتميزة' : 'A tour inside Oxygen Gym and discover our premium facilities'}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image, index) => (
            <motion.div
              key={image.id}
              className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer bg-gray-900 border border-white/5 hover:border-yellow-400/30 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onClick={() => {
                setCurrentIndex(index)
                setSelectedImage(image)
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              
{image.image_url ? (
                <img
                  src={image.image_url}
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-gray-600">
                  <div className="text-center">
                    <Camera className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <span className="text-sm opacity-50">{language === 'ar' ? 'صورة' : 'Image'} {image.id}</span>
                  </div>
                </div>
              )}

              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300">
                  <Camera className="w-8 h-8 text-black" />
                </div>
              </div>

              <div className="absolute bottom-0 right-0 left-0 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                <span className="text-yellow-400 text-sm font-medium">{image.category}</span>
                <h3 className="text-white text-lg font-bold mt-1">{image.title}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 left-4 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
              onClick={() => setSelectedImage(null)}
            >
              <X className="w-6 h-6" />
            </button>

            <button
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation()
                nextImage()
              }}
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <button
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
              onClick={(e) => {
                e.stopPropagation()
                prevImage()
              }}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            <motion.div
              className="relative max-w-4xl w-full aspect-video bg-gray-900 rounded-2xl flex items-center justify-center overflow-hidden"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
            >
{images[currentIndex]?.image_url ? (
                <img
                  src={images[currentIndex].image_url}
                  alt={images[currentIndex].title}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-center">
                  <Camera className="w-24 h-24 mx-auto mb-4 text-gray-600" />
                  <h3 className="text-2xl font-bold text-white mb-2">{images[currentIndex]?.title}</h3>
                  <p className="text-gray-400">{images[currentIndex]?.category}</p>
                </div>
              )}
            </motion.div>

            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center">
              <h3 className="text-xl font-bold text-white mb-1">{images[currentIndex]?.title}</h3>
              <p className="text-yellow-400">{images[currentIndex]?.category}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}

export default Gallery
